import { ErrorEvent, StackFrame, Breadcrumb, EventHint } from '@sentry/types';
import stringify from 'json-stringify-safe';

import { getBreakpoint } from 'bloko/common/media';

import pushAxiosDataToSentry from '../pushAxiosDataToSentry';
import { MAX_EXTRA_DATA_LENGTH, removeExtraData } from '../removeData';
import browserVersionsForSentryLogging from '../tools/generatedBrowserVersionsForSentryLogging';
import { Config, ExternalConfig, Environment, CachedError } from '../types';

const MAX_LAST_ACTION_PAYLOAD_LENGTH = MAX_EXTRA_DATA_LENGTH / 20;

interface UserAgentDetails {
    isSupported: true;
    browser: string;
}

declare global {
    // eslint-disable-next-line no-var
    var bloko: {
        getUserAgentDetails: (a: string, b: Record<string, number>) => UserAgentDetails;
    };
}

export const isBrowserSupported = (): boolean => {
    if (!window.bloko || !window.bloko.getUserAgentDetails) {
        return false;
    }
    const userAgentDetails = window.bloko.getUserAgentDetails(
        window.navigator.userAgent,
        browserVersionsForSentryLogging
    );
    return !!(userAgentDetails.isSupported && userAgentDetails.browser);
};

export const getEnvironment = (): Environment => {
    const re = window.navigator.userAgent.match(/test_host=(\w+)/);
    const isAutotests = !!re;
    const standName = re && re[1] ? re[1] : null;
    return { isAutotests, standName };
};

const parseSentryExternalConfig = (config: Config['sentryJsConfig']): ExternalConfig | null => {
    if (!config) {
        return null;
    }
    try {
        return JSON.parse(config) as ExternalConfig;
    } catch (e) {
        console.error('Sentry config could not be parsed', e);
        return null;
    }
};

const getIgnoreConfig = (
    type: 'ignoreErrors' | 'ignorePaths',
    externalConfig: ExternalConfig | null
): (string | RegExp)[] => {
    if (!externalConfig) {
        return [];
    }

    const ignoreConfig = externalConfig[type];
    if (!ignoreConfig) {
        return [];
    }

    const { strings = [], regexps = [] } = ignoreConfig;
    if (!strings.length && !regexps.length) {
        return [];
    }
    const filterRegexp = (regexp: RegExp | null): regexp is RegExp => !!regexp;
    const regexpObjects = regexps
        .map((regexp) => {
            try {
                if (typeof regexp === 'string') {
                    return new RegExp(regexp, 'i');
                }
                return new RegExp(regexp.pattern, regexp.flags);
            } catch (e) {
                console.error(e);
                return null;
            }
        })
        .filter(filterRegexp);
    return [...strings, ...regexpObjects];
};

const setFramesInAppFlag = (frames: StackFrame[] | undefined = []) => {
    const vendorFileParts = ['vendors.', 'magritte/', 'bloko/', '.yarn/', 'webpack'];
    // eslint-disable-next-line camelcase
    frames.forEach((frame) => (frame.in_app = !vendorFileParts.some((part) => frame.filename?.includes(part))));
};

const isErrorInCache = (errorMessage: string) => {
    const ERRORS_CACHE_KEY = '__jsErrorsCache';
    const ERRORS_CACHE_STORING_DURATION_MS = 24 * 60 * 60 * 1000; // 24 часа

    let errors: CachedError[];
    try {
        errors = JSON.parse(window.localStorage.getItem(ERRORS_CACHE_KEY) as string) as CachedError[];
    } catch (ignore) {
        errors = [];
    }

    if (!('isArray' in Array && Array.isArray(errors))) {
        errors = [];
    }

    const currentDate = Date.now();
    const relevantErrors = errors.filter(
        (cacheError) => cacheError.date + ERRORS_CACHE_STORING_DURATION_MS >= currentDate
    );

    const inCache = relevantErrors.some((errorCache) => errorCache.message === errorMessage);
    if (!inCache) {
        relevantErrors.push({
            date: currentDate,
            message: errorMessage,
        });

        try {
            window.localStorage.setItem(ERRORS_CACHE_KEY, JSON.stringify(relevantErrors));
        } catch (ignore) {} // eslint-disable-line no-empty
    }

    return inCache;
};

const getLastCall = (event: ErrorEvent) => {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    if (!frames.length) {
        return false;
    }
    const lastFrame = frames[frames.length - 1];
    if (lastFrame?.in_app === false) {
        return false;
    }
    return lastFrame.function ? String(lastFrame.function) : false;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialScope = ({
    getUser,
    getExtraData,
    environment,
}: {
    getUser: Config['getUser'];
    getExtraData: Config['getExtraData'];
    environment: Environment;
}) => {
    const user = getUser();
    const extra = getExtraData();
    return {
        user,
        extra,
        tags: {
            standName: environment.standName,
            userType: user.userType,
            pageName: String(extra.pageName),
        },
    };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const prepareConfig = ({
    sentryJsConfig,
    sentryDSN,
    buildVersion,
    staticHost,
    beforeSendCallback,
    getUser,
    getExtraData,
    isDebug,
}: Config) => {
    const parsedExternalConfig = parseSentryExternalConfig(sentryJsConfig);
    const ignoreErrors = getIgnoreConfig('ignoreErrors', parsedExternalConfig);
    const denyUrls = getIgnoreConfig('ignorePaths', parsedExternalConfig);
    const ignoreErrorsStacktrace = parsedExternalConfig?.ignoreErrors?.stacktraces;
    const replaysOnErrorSampleRate = parsedExternalConfig?.replaysOnErrorSampleRate ?? '0';
    const tracesSampleRate = parsedExternalConfig?.tracesSampleRate ?? '0';

    return {
        dsn: sentryDSN,
        release: buildVersion,
        ignoreErrors,
        denyUrls,
        includePaths: [staticHost, window.location.host, 'webpack-internal'],
        maxBreadcrumbs: 15,
        normalizeDepth: 8,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: parseFloat(replaysOnErrorSampleRate),
        tracesSampleRate: parseFloat(tracesSampleRate),
        beforeSend(event: ErrorEvent, hint: EventHint) {
            if (isDebug) {
                console.error('hint in beforeSend', hint);
                console.error('event in beforeSend:start', event);
            }
            if (document.body === null) {
                return null;
            }

            const extraData = getExtraData();

            let url = event?.request?.url;
            let lineNumber = 0;
            let errorMessage = event.message;

            if (event.exception) {
                const error = event.exception.values?.[0];
                errorMessage = error?.value || 'Unknown error message';
                if (error?.stacktrace) {
                    if (Array.isArray(ignoreErrorsStacktrace)) {
                        if (
                            ignoreErrorsStacktrace.some((stacktraceString) => {
                                const found = stacktraceString.match(/^(?<func>[^(]+)?(?:\(<(?<module>[^>]+)>\))?$/);

                                if (found && found.groups) {
                                    const { func, module } = found.groups;
                                    if (func || module) {
                                        return error.stacktrace?.frames?.some((frame) => {
                                            return (
                                                (func ? frame.function?.includes(func) : true) &&
                                                (module ? frame.filename?.includes(module) : true)
                                            );
                                        });
                                    }
                                }
                                return false;
                            })
                        ) {
                            return null;
                        }
                    }

                    const stacktrace = error.stacktrace.frames?.[error.stacktrace.frames.length - 1];
                    url = stacktrace?.filename;
                    lineNumber = stacktrace?.lineno || 0;
                    // Проставляем флаг - произошла ли ошибка в клиентском коде приложения
                    setFramesInAppFlag(error.stacktrace.frames);
                }
            }

            event = pushAxiosDataToSentry(event);
            const callbackResult = beforeSendCallback?.(event);
            if (callbackResult === false) {
                return null;
            }

            // Отсылаем ошибку одного типа только один раз в сутки.
            if (isErrorInCache(`${String(errorMessage)}|${String(url)}|${lineNumber}`)) {
                return null;
            }

            if (event.user) {
                event.user = getUser();
            }

            if (event.extra) {
                event.extra = {
                    ...event.extra,
                    ...extraData,
                    screenBreakpoint: getBreakpoint(),
                    documentStates: {
                        readyState: document.readyState,
                        visibilityState: document.visibilityState,
                        onLineState: window.navigator?.onLine,
                    },
                };

                // Убираем payload последнего action, если он тяжелый
                const reduxLastAction = event.extra.lastAction as { payload: Record<string, string> | string };
                if (reduxLastAction?.payload) {
                    const lastActionPayloadLength = stringify(reduxLastAction.payload).length;
                    if (lastActionPayloadLength > MAX_LAST_ACTION_PAYLOAD_LENGTH) {
                        reduxLastAction.payload = '[removed]';
                    }
                }

                removeExtraData(event);
            }

            if (!event.fingerprint && event.exception?.values?.[0]?.value) {
                event.fingerprint = [event.exception.values[0].value];
            }

            // Добавляем последний вызов из стека, чтобы разделить одни и те же события на разных элементах
            const lastCall = getLastCall(event);
            if (event.fingerprint && lastCall) {
                event.fingerprint.push(lastCall);
            }

            if (event.tags) {
                // Если ошибка произошла в коде html странице, то проставляем тег fromStatic: false
                event.tags.fromStatic = event.request?.url !== window.location.href;
                event.tags.breakpoint = getBreakpoint();
                event.tags.pageName = String(extraData.pageName);
            }

            if (isDebug) {
                console.error('event in beforeSend:end', event);
            }

            return event;
        },
        beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
            if (breadcrumb.category === 'xhr') {
                const url = breadcrumb.data?.url as string | undefined;
                if (!url?.startsWith('/')) {
                    return null;
                }
            }
            return breadcrumb;
        },
    };
};

export default {};
